class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');

    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );

    loadingSpinners.forEach((spinner) => {
      spinner.classList.remove('hidden');
    });

    const productGridContainer = document.getElementById('ProductGridContainer');
    const collection = productGridContainer?.querySelector('.collection');

    if (collection) {
      collection.classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};

    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');

    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';

    const currentCount =
      parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;

    const urlSearchParams = new URLSearchParams(searchParams);

    const isSearchPage =
      facetsContainer?.dataset.template === 'search';

    const isCollectionPage =
      facetsContainer?.dataset.template === 'collection';

    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage && SearchUpdateEvent) {
      deferred = SearchUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage && CollectionUpdateEvent) {
      /*
       * collectionId is null for auto-generated collections
       * (/collections/all, /collections/vendors/<x>, etc.).
       * The SSE schema accepts that.
       */
      deferred = CollectionUpdateEvent.createPromise();

      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: collectionId,
            handle: collectionHandle,
            productsCount: currentCount,
          },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({
          [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount,
        });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Facet request failed: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        const existingIndex = FacetFiltersForm.filterData.findIndex(
          (item) => item.url === url,
        );

        if (existingIndex !== -1) {
          FacetFiltersForm.filterData.splice(existingIndex, 1);
        }

        FacetFiltersForm.filterData.push({
          html,
          url,
        });

        if (
          FacetFiltersForm.filterData.length >
          FacetFiltersForm.maxCacheEntries
        ) {
          FacetFiltersForm.filterData.shift();
        }

        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const cachedData = FacetFiltersForm.filterData.find(filterDataUrl);

    if (!cachedData) return;

    FacetFiltersForm.renderSection(
      cachedData.html,
      event,
      updateEvent,
    );
  }

  /*
   * ----------------------------------------------------------
   * FACET AUTO-ADVANCE HELPERS
   * ----------------------------------------------------------
   */

  static getFacetAdvanceContext(event) {
    if (!event || !event.target) return null;

    const currentFacet = event.target.closest('.js-filter');

    if (!currentFacet) return null;

    const form = currentFacet.closest('form');

    if (!form) return null;

    const validFormIds = [
      'FacetFiltersForm',
      'FacetFiltersFormMobile',
      'FacetFiltersPillsForm',
    ];

    if (!validFormIds.includes(form.id)) {
      return null;
    }

    const facets = Array.from(
      form.querySelectorAll('.js-filter'),
    );

    const currentIndex = facets.indexOf(currentFacet);

    if (currentIndex === -1) return null;

    return {
      formId: form.id,
      facetId: currentFacet.id,
      currentIndex,
    };
  }

  static getFacetContainer(formId) {
    if (formId === 'FacetFiltersForm') {
      return document.getElementById('FacetFiltersForm');
    }

    if (formId === 'FacetFiltersFormMobile') {
      return document.getElementById('FacetFiltersFormMobile');
    }

    if (formId === 'FacetFiltersPillsForm') {
      return document.getElementById('FacetFiltersPillsForm');
    }

    return null;
  }

  static isFacetAvailable(facet) {
    if (!facet) return false;

    if (facet.hidden) return false;

    if (facet.hasAttribute('disabled')) return false;

    if (facet.classList.contains('disabled')) return false;

    const summary = facet.querySelector('summary');

    if (!summary) return false;

    if (summary.hasAttribute('disabled')) return false;

    if (summary.getAttribute('aria-disabled') === 'true') {
      return false;
    }

    return true;
  }

  static openNextFacet(context) {
    if (!context) return;

    const form = FacetFiltersForm.getFacetContainer(
      context.formId,
    );

    if (!form) return;

    const facets = Array.from(
      form.querySelectorAll('.js-filter'),
    );

    if (!facets.length) return;

    /*
     * Find the current facet again AFTER AJAX rendering.
     */
    let currentIndex = -1;

    if (context.facetId) {
      currentIndex = facets.findIndex(
        (facet) => facet.id === context.facetId,
      );
    }

    /*
     * If the current facet was removed after filtering,
     * use its previous DOM position as fallback.
     */
    if (
      currentIndex === -1 &&
      Number.isInteger(context.currentIndex)
    ) {
      currentIndex = Math.min(
        context.currentIndex,
        facets.length - 1,
      );
    }

    if (currentIndex === -1) return;

    const currentFacet = facets[currentIndex];

    /*
     * Find the NEXT available facet based on the
     * actual rendered DOM order.
     */
    const nextFacet = facets
      .slice(currentIndex + 1)
      .find((facet) =>
        FacetFiltersForm.isFacetAvailable(facet),
      );

    /*
     * --------------------------------------------------------
     * MOBILE
     * --------------------------------------------------------
     *
     * Dawn mobile filters are inside <menu-drawer>.
     *
     * IMPORTANT:
     * We do NOT call menuDrawer.onSummaryClick() directly.
     *
     * Dawn's onSummaryClick() is designed to run as a
     * listener for a REAL summary click. The browser's
     * native <summary> click is responsible for toggling
     * <details open>.
     *
     * Therefore we use summary.click().
     */
    if (context.formId === 'FacetFiltersFormMobile') {
      const menuDrawer = form.closest('menu-drawer');

      if (!menuDrawer) return;

      /*
       * No next facet:
       * simply close the current mobile submenu.
       */
      if (!nextFacet) {
        if (
          currentFacet?.open &&
          typeof menuDrawer.closeSubmenu === 'function'
        ) {
          menuDrawer.closeSubmenu(currentFacet);
        }

        return;
      }

      /*
       * Close the currently opened mobile facet first.
       */
      if (
        currentFacet?.open &&
        typeof menuDrawer.closeSubmenu === 'function'
      ) {
        menuDrawer.closeSubmenu(currentFacet);

        /*
         * Dawn closeAnimation() waits approximately 400ms
         * before removing the "open" attribute.
         *
         * Wait slightly longer before triggering the next
         * facet so both submenu animations do not overlap.
         */
        setTimeout(() => {
          const currentForm = document.getElementById(
            'FacetFiltersFormMobile',
          );

          if (!currentForm) return;

          /*
           * Re-find the next facet from the CURRENT DOM.
           * This is important because AJAX may have replaced
           * its HTML after the filter selection.
           */
          const refreshedNextFacet =
            currentForm.querySelector(
              `#${CSS.escape(nextFacet.id)}`,
            );

          if (!refreshedNextFacet) return;

          if (
            !FacetFiltersForm.isFacetAvailable(
              refreshedNextFacet,
            )
          ) {
            return;
          }

          if (refreshedNextFacet.open) return;

          const nextSummary =
            refreshedNextFacet.querySelector('summary');

          if (!nextSummary) return;

          /*
           * Use the REAL native click.
           *
           * Do NOT call:
           * menuDrawer.onSummaryClick(...)
           *
           * because that bypasses the browser's native
           * <summary>/<details> toggle behavior.
           */
          nextSummary.click();
        }, 450);

        return;
      }

      /*
       * If the current facet is already closed,
       * open the next facet immediately using the
       * native summary click.
       */
      const nextSummary = nextFacet.querySelector('summary');

      if (!nextSummary) return;

      if (!nextFacet.open) {
        nextSummary.click();
      }

      return;
    }

    /*
     * --------------------------------------------------------
     * DESKTOP
     * --------------------------------------------------------
     *
     * Desktop Dawn facets use normal <details>.
     *
     * Close current facet first, then open the next one.
     */
    if (currentFacet) {
      currentFacet.open = false;
    }

    if (!nextFacet) return;

    const nextSummary = nextFacet.querySelector('summary');

    if (!nextSummary) return;

    if (!nextFacet.open) {
      nextSummary.click();
    }
  }

  /*
   * ----------------------------------------------------------
   * RENDER SECTION
   * ----------------------------------------------------------
   */

  static renderSection(html, event, updateEvent) {
    /*
     * Capture the selected facet BEFORE renderFilters()
     * replaces/updates the existing facet DOM.
     */
    const facetAdvanceContext =
      FacetFiltersForm.getFacetAdvanceContext(event);

    /*
     * Keep Dawn's existing filtering/rendering flow unchanged.
     */
    FacetFiltersForm.renderFilters(html, event);

    FacetFiltersForm.renderProductGridContainer(html);

    FacetFiltersForm.renderProductCount(
      html,
      updateEvent,
    );

    if (
      typeof initializeScrollAnimationTrigger === 'function'
    ) {
      initializeScrollAnimationTrigger(html.innerHTML);
    }

    /*
     * Open the next facet ONLY after the new facet DOM
     * has been rendered.
     *
     * This now applies to BOTH desktop and mobile.
     */
    if (facetAdvanceContext) {
      requestAnimationFrame(() => {
        FacetFiltersForm.openNextFacet(
          facetAdvanceContext,
        );
      });
    }
  }

  static renderProductGridContainer(html) {
    const productGridContainer =
      document.getElementById(
        'ProductGridContainer',
      );

    if (!productGridContainer) return;

    const parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html');

    const newProductGrid =
      parsedHTML.getElementById(
        'ProductGridContainer',
      );

    if (!newProductGrid) return;

    productGridContainer.innerHTML =
      newProductGrid.innerHTML;

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add(
          'scroll-trigger--cancel',
        );
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser()
      .parseFromString(html, 'text/html');

    const sourceCount =
      parsedHtml.getElementById('ProductCount');

    if (!sourceCount) {
      updateEvent?.resolve(0);
      return;
    }

    const count = sourceCount.innerHTML;

    const container =
      document.getElementById('ProductCount');

    const containerDesktop =
      document.getElementById(
        'ProductCountDesktop',
      );

    if (container) {
      container.innerHTML = count;

      container.dataset.productCount =
        sourceCount.dataset.productCount || '';

      container.dataset.totalCount =
        sourceCount.dataset.totalCount || '';

      container.classList.remove('loading');
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );

    loadingSpinners.forEach((spinner) => {
      spinner.classList.add('hidden');
    });

    updateEvent?.resolve(
      parseInt(
        sourceCount.dataset.productCount,
      ) || 0,
    );
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html');

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
      );

    /*
     * Remove facets that no longer exist in the
     * server-rendered response.
     */
    Array.from(facetDetailsElementsFromDom).forEach(
      (currentElement) => {
        if (
          !Array.from(
            facetDetailsElementsFromFetch,
          ).some(
            ({ id }) =>
              currentElement.id === id,
          )
        ) {
          currentElement.remove();
        }
      },
    );

    const matchesId = (element) => {
      const jsFilter = event
        ? event.target.closest('.js-filter')
        : undefined;

      return jsFilter
        ? element.id === jsFilter.id
        : false;
    };

    const facetsToRender =
      Array.from(
        facetDetailsElementsFromFetch,
      ).filter(
        (element) => !matchesId(element),
      );

    const countsToRender =
      Array.from(
        facetDetailsElementsFromFetch,
      ).find(matchesId);

    facetsToRender.forEach(
      (elementToRender, index) => {
        const currentElement =
          document.getElementById(
            elementToRender.id,
          );

        /*
         * Existing facet:
         * update only its inner HTML.
         */
        if (currentElement) {
          currentElement.innerHTML =
            elementToRender.innerHTML;
        } else {
          /*
           * New facet:
           * insert it in the correct position.
           */
          if (index > 0) {
            const {
              className:
                previousElementClassName,
              id: previousElementId,
            } = facetsToRender[index - 1];

            /*
             * Same facet type
             * (horizontal/vertical/mobile drawer).
             */
            if (
              elementToRender.className ===
              previousElementClassName
            ) {
              const previousElement =
                document.getElementById(
                  previousElementId,
                );

              if (previousElement) {
                previousElement.after(
                  elementToRender,
                );

                return;
              }
            }
          }

          if (elementToRender.parentElement) {
            const parentId =
              elementToRender.parentElement.id;

            const firstFacet =
              document.querySelector(
                `#${CSS.escape(parentId)} .js-filter`,
              );

            if (firstFacet) {
              firstFacet.before(
                elementToRender,
              );
            }
          }
        }
      },
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML,
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML,
    );

    /*
     * Re-render counts/state for the facet that
     * triggered the filtering request.
     */
    if (countsToRender && event?.target) {
      const originatingFacet =
        event.target.closest('.js-filter');

      if (!originatingFacet) return;

      const closestJSFilterID =
        originatingFacet.id;

      if (!closestJSFilterID) return;

      FacetFiltersForm.renderCounts(
        countsToRender,
        originatingFacet,
      );

      const refreshedFacet =
        document.getElementById(
          closestJSFilterID,
        );

      if (refreshedFacet) {
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          refreshedFacet,
        );
      }

      /*
       * Restore focus to the same checkbox/input
       * where possible.
       */
      if (refreshedFacet) {
        const isTextInput =
          event.target.getAttribute('type') ===
          'text';

        if (!isTextInput) {
          const originatingInputId =
            event.target.id;

          const matchingInput =
            originatingInputId
              ? refreshedFacet.querySelector(
                  `#${CSS.escape(
                    originatingInputId,
                  )}`,
                )
              : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            const fallbackSelector =
              refreshedFacet.classList.contains(
                'mobile-facets__details',
              )
                ? '.mobile-facets__close-button'
                : '.facets__summary';

            const fallbackElement =
              refreshedFacet.querySelector(
                fallbackSelector,
              );

            if (fallbackElement) {
              fallbackElement.focus();
            }
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const activeFacetsElement =
          html.querySelector(selector);

        if (!activeFacetsElement) return;

        const currentElement =
          document.querySelector(selector);

        if (!currentElement) return;

        currentElement.innerHTML =
          activeFacetsElement.innerHTML;
      },
    );

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach(
      (selector) => {
        const sourceElement =
          html.querySelector(selector);

        const currentElement =
          document.querySelector(selector);

        if (!sourceElement || !currentElement) {
          return;
        }

        currentElement.innerHTML =
          sourceElement.innerHTML;
      },
    );

    /*
     * Re-bind Dawn's mobile drawer events after
     * AJAX has updated the facet markup.
     *
     * IMPORTANT:
     * global.js bindEvents() should be idempotent
     * so existing elements do not receive duplicate
     * click listeners.
     */
    const mobileForm =
      document.getElementById(
        'FacetFiltersFormMobile',
      );

    const menuDrawer =
      mobileForm?.closest('menu-drawer');

    if (
      menuDrawer &&
      typeof menuDrawer.bindEvents === 'function'
    ) {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary =
      target.querySelector(
        '.facets__summary',
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary',
      );

    if (
      sourceSummary &&
      targetSummary
    ) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header',
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header',
      );

    if (
      sourceHeaderElement &&
      targetHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector(
        '.facets-wrap',
      );

    const sourceWrapElement =
      source.querySelector(
        '.facets-wrap',
      );

    if (
      sourceWrapElement &&
      targetWrapElement
    ) {
      const isShowingMore =
        Boolean(
          target.querySelector(
            'show-more-button .label-show-more.hidden',
          ),
        );

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(
            '.facets__item.hidden',
          )
          .forEach(
            (hiddenItem) => {
              hiddenItem.classList.replace(
                'hidden',
                'show-more-item',
              );
            },
          );
      }

      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;

    const targetFacetsList =
      target.querySelector(
        '.mobile-facets__list',
      );

    const sourceFacetsList =
      source.querySelector(
        '.mobile-facets__list',
      );

    if (
      sourceFacetsList &&
      targetFacetsList
    ) {
      targetFacetsList.outerHTML =
        sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${
        searchParams
          ? '?'.concat(searchParams)
          : ''
      }`,
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid',
      );

    if (!productGrid?.dataset.id) {
      return [];
    }

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData =
      new FormData(form);

    return new URLSearchParams(
      formData,
    ).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(
      searchParams,
      event,
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form',
      );

    if (
      event.srcElement.className ===
      'mobile-facets__checkbox'
    ) {
      const form =
        event.target.closest('form');

      if (!form) return;

      const searchParams =
        this.createSearchParams(form);

      this.onSubmitForm(
        searchParams,
        event,
      );
    } else {
      const forms = [];

      const targetForm =
        event.target.closest('form');

      if (!targetForm) return;

      const isMobile =
        targetForm.id ===
        'FacetFiltersFormMobile';

      sortFilterForms.forEach(
        (form) => {
          if (!isMobile) {
            if (
              form.id ===
                'FacetSortForm' ||
              form.id ===
                'FacetFiltersForm' ||
              form.id ===
                'FacetSortDrawerForm'
            ) {
              forms.push(
                this.createSearchParams(
                  form,
                ),
              );
            }
          } else if (
            form.id ===
            'FacetFiltersFormMobile'
          ) {
            forms.push(
              this.createSearchParams(
                form,
              ),
            );
          }
        },
      );

      this.onSubmitForm(
        forms.join('&'),
        event,
      );
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') ===
      -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') +
              1,
          );

    FacetFiltersForm.renderPage(
      url,
    );
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  'facet-filters-form',
  FacetFiltersForm,
);

FacetFiltersForm.setListeners();


/* ==========================================================
   PRICE RANGE
========================================================== */

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach(
      (element) => {
        element.addEventListener(
          'change',
          this.onRangeChange.bind(this),
        );

        element.addEventListener(
          'keydown',
          this.onKeyDown.bind(this),
        );
      },
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(
      event.currentTarget,
    );

    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!minInput || !maxInput) return;

    if (maxInput.value) {
      minInput.setAttribute(
        'data-max',
        maxInput.value,
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'data-min',
        minInput.value,
      );
    }

    if (minInput.value === '') {
      maxInput.setAttribute(
        'data-min',
        0,
      );
    }

    if (maxInput.value === '') {
      minInput.setAttribute(
        'data-max',
        maxInput.getAttribute(
          'data-max',
        ),
      );
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(
      input.getAttribute('data-min'),
    );
    const max = Number(
      input.getAttribute('data-max'),
    );

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}

customElements.define(
  'price-range',
  PriceRange,
);


/* ==========================================================
   FACET REMOVE
========================================================== */

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute(
      'role',
      'button',
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this),
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(event);
        }
      },
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form',
      ) ||
      document.querySelector(
        'facet-filters-form',
      );

    if (!form) return;

    form.onActiveFilterClick(
      event,
    );
  }
}

customElements.define(
  'facet-remove',
  FacetRemove,
);
